import { useEffect } from "react";
import "../assets/styles/LandingPage20.css";
import Footer20 from "../components/common/Footer20";
import BotaoContato from "../components/common/BotaoContato";

/* ── HOOK ── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".st-reveal");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ── DATA ── */
const steps = [
  { num: "01", title: "Integre em minutos",      desc: "Continue usando o WhatsApp web e o celular normalmente. Zero interrupção no seu fluxo atual." },
  { num: "02", title: "IA assistida inteligente", desc: "A IA pede ajuda ao humano apenas quando não souber responder — sem erros, sem experiências ruins." },
  { num: "03", title: "Atendimento sob comando",  desc: "Você decide quando a IA age. Configure para iniciar conversas só quando e como quiser." },
  { num: "04", title: "Fluxos inteligentes",      desc: "Respostas automáticas, triagem e transferência para atendentes humanos no momento certo." },
  { num: "05", title: "Qualquer idioma",          desc: "Tradução automática e instantânea. Atenda clientes do mundo inteiro sem barreiras." },
  { num: "06", title: "Escale sem contratar",     desc: "Atenda centenas ou milhares de clientes com a mesma equipe de hoje." },
];

const benefits = [
  { icon: "⚡", label: "Integração rápida e simples" },
  { icon: "🌐", label: "Tradução automática em tempo real" },
  { icon: "👥", label: "Escalabilidade sem aumentar a equipe" },
  { icon: "⏱️", label: "Redução drástica no tempo de resposta" },
];

const faqs = [
  { icon: "💬", q: "Funciona só no WhatsApp?",           a: "Não. Também integramos com Instagram, Facebook Messenger e chat no site — tudo em uma única plataforma." },
  { icon: "⚡", q: "Preciso saber programar?",            a: "Não. A configuração é simples e guiada. Você ativa, configura e já está atendendo." },
  { icon: "🌐", q: "E se meu cliente falar outro idioma?", a: "O sistema traduz automaticamente a conversa em tempo real, para você e para o cliente." },
];

const teaserProducts = [
  {
    name: "Lead",
    nameAccent: "IA",
    desc: "Monitora o feed do Instagram, comenta com mensagens aprovadas e converte interações em leads — automaticamente, com registro completo no CRM.",
    chips: ["Instagram", "Geração de Leads", "CRM"],
    link: "/products/leadia",
    linkLabel: "Conhecer LeadIA →",
    dot: "#06b6d4",
  },
  {
    name: "NutrI",
    nameAccent: "A",
    desc: "Cultiva cada lead com sequências personalizadas pelo WhatsApp — e avisa seu time exatamente quando o momento de fechar chegou.",
    chips: ["WhatsApp", "Nutrição", "Follow-up"],
    link: "/products/nutria",
    linkLabel: "Conhecer NutrIA →",
    dot: "#22c55e",
  },
  {
    name: "Athen",
    nameAccent: "IA",
    desc: "Painel de inteligência que consolida dados de todos os produtos em tempo real — com alertas automáticos e relatórios em PDF.",
    chips: ["Dashboard", "Métricas", "Relatórios"],
    link: "/products/athenia",
    linkLabel: "Conhecer AthenIA →",
    dot: "#a78bfa",
  },
];

/* ── COMPONENTS ── */
function Navbar() {
  return (
    <nav className="st-nav">
      <a href="/" className="st-logo">Summer<span>Tech</span></a>
      <a href="#cta" className="st-nav-cta">Começar agora →</a>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="st-hero">
      <div className="st-hero-glow" />
      <div className="st-hero-glow2" />
      <div className="st-hero-inner">
        <span className="st-hero-badge"><span className="st-badge-dot" />Inteligência Artificial para Atendimento</span>
        <h1>
          Atenda mais.<br />
          <span className="st-accent">Contrate menos.</span><br />
          Cresça sem limites.
        </h1>
        <p>Automatize seu atendimento no WhatsApp com IA — responda em qualquer idioma, 24h por dia, sem aumentar sua equipe.</p>
        <div className="st-hero-actions">
          <a href="#cta" className="st-btn-primary">Quero começar agora</a>
          <a href="#como" className="st-btn-ghost">Ver como funciona</a>
        </div>
        <div className="st-hero-stats">
          {[
            { num: "24/7", label: "Atendimento ininterrupto" },
            { num: "+50",  label: "Idiomas suportados" },
            { num: "0",    label: "Horas de fila de espera" },
          ].map((s) => (
            <div key={s.label}>
              <span className="st-stat-num">{s.num}</span>
              <span className="st-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SobreSection() {
  return (
    <section className="st-section st-sobre">
      <div className="st-wrap">
        <div className="st-sobre-grid st-reveal">
          <div>
            <span className="st-tag">Quem Somos</span>
            <h2 className="st-title">IA estratégica para qualquer negócio</h2>
            <p className="st-text">Desenvolvemos soluções inovadoras com inteligência artificial para aumentar a eficiência, reduzir custos e melhorar a experiência de clientes e equipes. Acreditamos que a IA é um recurso estratégico acessível a empresas de todos os tamanhos.</p>
          </div>
          <div className="st-sobre-visual">
            <div className="st-sobre-card c1">
              <div className="st-card-icon">🤖</div>
              <div className="st-card-title">IA que aprende com seu negócio</div>
              <div className="st-card-text">Configurável para o tom, contexto e necessidades da sua empresa.</div>
            </div>
            <div className="st-sobre-card c2">
              <div className="st-card-icon">⚡</div>
              <div className="st-card-title">Integração em minutos</div>
              <div className="st-card-text">Nenhuma linha de código necessária. Conecte e ative.</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ComoSection() {
  return (
    <section className="st-section st-como" id="como">
      <div className="st-wrap">
        <span className="st-tag">Como Funciona</span>
        <h2 className="st-title">Simples de configurar.<br />Poderoso na prática.</h2>
        <div className="st-steps-grid">
          {steps.map((s) => (
            <div className="st-step-card st-reveal" key={s.num}>
              <div className="st-step-num">{s.num}</div>
              <div className="st-step-title">{s.title}</div>
              <div className="st-step-desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProblemaSection() {
  return (
    <section className="st-section st-problema">
      <div className="st-wrap">
        <div className="st-problema-inner st-reveal">
          <div className="st-problema-quote">"Poucos atendentes para muitos clientes — oportunidades se perdendo a cada minuto."</div>
          <div className="st-problema-text">
            <span className="st-tag">O Desafio</span>
            <p className="st-text">No WhatsApp, redes sociais e outros canais, filas de espera se formam, clientes desistem e negócios perdem receita. Não por falta de produto — por falta de capacidade de atendimento.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function SolucaoSection() {
  return (
    <section className="st-section st-solucao">
      <div className="st-wrap">
        <div className="st-solucao-grid st-reveal">
          <div>
            <span className="st-tag">Nossa Solução</span>
            <h2 className="st-solucao-heading">Atendimento escalável, multilíngue e integrado</h2>
            <p className="st-solucao-desc">Nosso produto conecta-se ao seu WhatsApp para automatizar respostas, priorizar demandas e eliminar filas. Entende qualquer idioma e sabe quando acionar um humano.</p>
            <ul className="st-benefits">
              {benefits.map((b) => (
                <li key={b.label}><span className="st-benefit-icon">{b.icon}</span>{b.label}</li>
              ))}
            </ul>
            <a href="#cta" className="st-btn-primary">Quero conhecer</a>
          </div>
          <div className="st-sol-visual">
            <div className="st-sol-card"><span className="st-sol-num">98%</span><span className="st-sol-label">Taxa de resolução automática</span></div>
            <div className="st-sol-card"><span className="st-sol-num">&lt;1s</span><span className="st-sol-label">Tempo médio de resposta</span></div>
            <div className="st-sol-card span2"><span className="st-sol-num">50+</span><span className="st-sol-label">Idiomas com tradução automática em tempo real</span></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductsTeaser() {
  return (
    <section className="st-section st-produtos">
      <div className="st-wrap">
        <span className="st-tag">Ecossistema SummerTech</span>
        <h2 className="st-title">JulIA é só o começo.</h2>
        <p className="st-text" style={{ marginBottom: "3rem" }}>
          Um ecossistema completo de IA que acompanha seu cliente do primeiro contato até a conversão — e te mostra tudo em tempo real.
        </p>

        {/* Card principal: JulIA */}
        <div className="st-prod-main st-reveal" style={{ marginBottom: "1.5rem" }}>
          <div>
            <span className="st-prod-badge">Produto principal</span>
            <h3 className="st-prod-name"><span style={{ color: "var(--yellow)" }}>Jul</span>IA</h3>
            <p className="st-prod-desc">Sua atendente virtual 24/7 no WhatsApp. Responde, traduz e só aciona um humano quando necessário — para que sua equipe foque no que realmente importa.</p>
            <div className="st-prod-chips">
              {["WhatsApp", "24/7", "Multilíngue", "IA assistida"].map((c) => (
                <span className="st-prod-chip" key={c}>{c}</span>
              ))}
            </div>
            <a href="#cta" className="st-prod-link">Começar com JulIA →</a>
          </div>
          <div className="st-prod-visual">
            <div className="st-prod-stat"><span className="st-prod-stat-num">24/7</span><span className="st-prod-stat-lbl">Atendimento contínuo</span></div>
            <div className="st-prod-stat"><span className="st-prod-stat-num">98%</span><span className="st-prod-stat-lbl">Resolução automática</span></div>
            <div className="st-prod-stat wide"><span className="st-prod-stat-num">50+</span><span className="st-prod-stat-lbl">Idiomas com tradução em tempo real</span></div>
          </div>
        </div>

        {/* Cards dos 3 novos produtos */}
        <div className="st-teaser-trio st-reveal">
          {teaserProducts.map((p) => (
            <div className="st-prod-teaser" key={p.name}>
              <span className="st-new-badge">
                <span className="st-new-dot" style={{ background: p.dot }} />
                Novo produto
              </span>
              <div>
                <h3 className="st-prod-name-sm">
                  <span style={{ color: "var(--yellow)" }}>{p.name}</span>{p.nameAccent}
                </h3>
                <p className="st-prod-desc-sm">{p.desc}</p>
              </div>
              <hr className="st-teaser-divider" />
              <div className="st-prod-chips">
                {p.chips.map((c) => (
                  <span className="st-prod-chip" key={c}>{c}</span>
                ))}
              </div>
              <a href={p.link} className="st-prod-link-ghost">{p.linkLabel}</a>
            </div>
          ))}
        </div>

        {/* Banner de ecossistema */}
        <div className="st-ecosystem-banner st-reveal">
          <div className="st-ecosystem-text">
            <span className="st-ecosystem-label">Ecossistema completo</span>
            <p>Use todos os produtos juntos e tenha uma operação de vendas e atendimento totalmente automatizada — do primeiro lead ao fechamento.</p>
          </div>
          <div className="st-ecosystem-flow">
            {[
              { name: "LeadIA", icon: "🎯", desc: "Capta" },
              { name: "JulIA",  icon: "💬", desc: "Atende" },
              { name: "NutrIA", icon: "🌱", desc: "Nutre" },
              { name: "AthenIA",icon: "📊", desc: "Analisa" },
            ].map((p, i, arr) => (
              <div key={p.name} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <div className="st-eco-step">
                  <span className="st-eco-icon">{p.icon}</span>
                  <span className="st-eco-name">{p.name}</span>
                  <span className="st-eco-desc">{p.desc}</span>
                </div>
                {i < arr.length - 1 && <span className="st-eco-arrow">→</span>}
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section className="st-section st-faq">
      <div className="st-wrap">
        <span className="st-tag">Dúvidas Frequentes</span>
        <h2 className="st-title">Perguntas &amp;<br />Respostas</h2>
        <div className="st-faq-grid">
          {faqs.map((f) => (
            <div className="st-faq-card st-reveal" key={f.q}>
              <div className="st-faq-q"><span className="st-faq-ico">{f.icon}</span>{f.q}</div>
              <div className="st-faq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="st-section st-cta" id="cta">
      <div className="st-wrap">
        <div className="st-cta-inner">
          <h2>Comece agora.<br />Transforme hoje.</h2>
          <p>Use nossa solução e acompanhe sua empresa crescer.</p>
          <BotaoContato className="st-btn-dark" text="Quero começar agora →" />
        </div>
      </div>
    </section>
  );
}

/* ── MAIN EXPORT ── */
export default function LandingPage() {
  useReveal();
  return (
    <>
      <Navbar />
      <HeroSection />
      <SobreSection />
      <ComoSection />
      <ProblemaSection />
      <SolucaoSection />
      <ProductsTeaser />
      <FaqSection />
      <CtaSection />
      <Footer20 className="st-footer"/>
    </>
  );
}